import React, { forwardRef, MutableRefObject, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { colors, glyphs, frames, getGraphColor } from "../../../Styles";
import { NewInput, NewInputList, NewSelect, SecondarySetting } from "../..";
import { currentSubContentTypes, useModalContext } from "../../GlobalModals/_GlobalModals/globalModalContext";
import { ListStyle_Types, NewFieldStyles, NewInputHandles, NewInputListHandles, NewSelectHandles } from "../../../Definitions";
import { dispatchFetchStationConfigById, SelectAllStationConfigs, selectStationConfigByStationId } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/stationConfigSlice";
import { MutateStationConfigPayload, useStationConfig } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/useStationConfig";
import { StoreAppDispatch, useAppDispatch, useAppSelector } from "../../../Store";
import { showSuccessWithTimeout } from "../../../Store/Thunks/successWithTimeout";
import { isEqual } from "lodash";
import { Station } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/queryStations";
import { useDeviceInfo, useFeedComponents, useOldSection, useTerminals } from "../../../Services";
import { TerminalInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getTerminalsCall";
import { StationConfig } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/queryStationConfigs";

const _fieldStyle = NewFieldStyles.greyTertiary;
const _fieldWidth = undefined;
const _glyphColor = colors.greyDarkPrimary;

interface settingsModal_Props {
  station: Station;
}
export interface SettingsContentHandles {
  save: () => void;
}

const SettingsContent = forwardRef<SettingsContentHandles, settingsModal_Props>(({ station }, ref) => {
  const { t } = useTranslation();
  const { state, dispatch } = useModalContext();
  const { mutateConfig } = useStationConfig();
  const [terminalComponents, setTerminalComponents] = useState<Record<number, number | null>>({});
  const dispatchStationCongif = useAppDispatch();
  const dispatchSuccessMode = useDispatch<StoreAppDispatch>();

  // #region | Data
  const { call: getTerminals, data: terminalsState, } = useTerminals(state.connection);
  const { call: getFeedComponentsInfo, data: feedComponentsState } = useFeedComponents(state.connection);
  const { maintainSectionLocations } = useOldSection(state.connection);

  const currentStationConfigs = useAppSelector((state) => selectStationConfigByStationId(state, [station.station_id]), isEqual)
  const getFieldValue = (type: number) => currentStationConfigs?.find(c => c.value_type === type)?.value ?? "";
  const getFieldValue2ElectricBogaloo = (type: number, key: keyof StationConfig) => currentStationConfigs?.find(c => c.value_type === type)?.[key] ?? "";


  const terminals = useMemo(() => terminalsState[state.domain] && terminalsState[state.domain].data, [state.domain, terminalsState])
  const stationTerminals = useMemo(() => terminals?.filter(t => station.terminals.includes(t.terminal_id)).filter(t => t.fk_terminal_types === 83) ?? [], [terminals])
  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[state.domain] ? feedComponentsState[state.domain].data: undefined,[state.domain, feedComponentsState])

  useEffect(() => {
    getTerminals({});
    getFeedComponentsInfo({fetchAmount: 50}, state.connection);
  }, [state.connection])
  // #endregion

  useEffect(() => {
    dispatchStationCongif(dispatchFetchStationConfigById(station.station_id))
  }, [dispatch, station])

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  // Helper Function for Motor Components
  const getSelectedComponent = (terminal: TerminalInfo) => {
    const selectedId =
      terminalComponents[terminal.terminal_id] ?? terminal.fk_components;
  
    return feedComponents?.find(c => c.id === selectedId);
  };

  // #region | Handle Save
  const handleSave = () => {
    const values = fields.map(f => {
      const value =
        f.index === null
          ? f.ref.current?.value
          : f.ref.current?.refs.current[f.index]?.value;
  
      return { ...f, value };
    });
    for (const f of values) {

      if (!f.value) {
        dispatch({message: (t("", { defaultValue: f.label }))})
        return;
      }
    }
  
    const payload = values.reduce((agg, f) => {

      const currentEntry = agg[f.type] ?? {
        stationId: station.station_id,
        valueType: f.type,
        value: 0
      }
      currentEntry[f.key] = Number(f.value)
      agg[f.type] = currentEntry;
      return agg;
    }, {} as Record<number, MutateStationConfigPayload>);

    const terminalPayload = stationTerminals.map(t => {
      const newComponent = terminalComponents[t.terminal_id];
      const updated = newComponent ?? t.fk_components ?? null;
  
      if (updated === (t.fk_components ?? null)) return null;
  
      return {
        terminal_id: t.terminal_id,
        ...(updated != null && { fk_components: updated })
      };
    }).filter((t): t is { terminal_id: number; fk_components?: number } => t !== null);
  
    maintainSectionLocations([], [], [], terminalPayload);
    mutateConfig(Object.values(payload));
    dispatch({ currentSubContent: currentSubContentTypes.overview })
    dispatchSuccessMode(showSuccessWithTimeout("success"))
  };
  // #endregion

  // #region | Fields Refs
  const waterRef = useRef<NewInputListHandles | null>(null);
  const feedMotorsRef = useRef<NewInputListHandles | null>(null);
  const gateMotorRef = useRef<NewInputListHandles | null>(null);

  const fields: {ref: MutableRefObject<NewInputListHandles | null>, index: number, type: number, label: string, key: keyof MutateStationConfigPayload}[] = [
    { ref: waterRef, index: 0, type: 1, label: `${t("esfWalkThroughPages.sections.waterAddedPrCup", {defaultValue: "water added pr. cup"})} ${t("esfWalkThroughPages.sections.missing", {defaultValue: "missing"})}`, key: "value" },
    { ref: waterRef, index: 1, type: 1, label: `${t("esfWalkThroughPages.sections.waterDelay", {defaultValue: "water delay"})} ${t("esfWalkThroughPages.sections.missing", {defaultValue: "missing"})}`, key: "valueMin" },
    ...(station.stationType === 2 ? [
      { ref: gateMotorRef, index: 0, type: 4, label: `${t("esfWalkThroughPages.sections.gateAdjustment", { defaultValue: "gate adjustment" })} ${t("esfWalkThroughPages.sections.missing", { defaultValue: "missing" })}`, key: "value" },
      { ref: gateMotorRef, index: 1, type: 4, label: `${t("esfWalkThroughPages.sections.delayTimeClosingGate", {defaultValue: "delay time closing gate"})} ${t("esfWalkThroughPages.sections.missing", {defaultValue: "missing"})}`, key: "valueMin" },
      { ref: gateMotorRef, index: 2, type: 2, label: `${t("esfWalkThroughPages.sections.indetificationValidTime", {defaultValue: "identification valid time"})} ${t("esfWalkThroughPages.sections.missing", {defaultValue: "missing"})}`, key: "valueMin" },
      { ref: gateMotorRef, index: 3, type: 2, label: `${t("esfWalkThroughPages.sections.entryTime", {defaultValue: "entry time"})} ${t("esfWalkThroughPages.sections.missing", {defaultValue: "missing"})}`, key: "valueMax" },
    ] as const : [] ),
    { ref: feedMotorsRef, index: 0, type: 3, label: `${t("esfWalkThroughPages.sections.noFeedBlockTime", {defaultValue: "no feed block time"})} ${t("esfWalkThroughPages.sections.missing", {defaultValue: "missing"})}`, key: "valueMin" },
    { ref: feedMotorsRef, index: 1, type: 3, label: `${t("esfWalkThroughPages.sections.lastFeedBlockTime", {defaultValue: "last feed block time"})} ${t("esfWalkThroughPages.sections.missing", {defaultValue: "missing"})}`, key: "valueMax" },
  ];
  // #endregion

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Water */}
      <SecondarySetting
        key={station.station_id}
        header={t("esfWalkThroughPages.sections.water", {defaultValue: "water"})}
        startEnhancer={glyphs.Setting({ fillColor: _glyphColor })}
        content={
          <NewInputList
            ref={waterRef}
            placeholders={Array(2).fill("")}
            headers={[`${t("esfWalkThroughPages.sections.waterAddedPrCup", {defaultValue: "water added pr. cup"})} (${t("units.milisec", {defaultValue: "milisec"})}.)`, `${t("esfWalkThroughPages.sections.waterDelay", {defaultValue: "water delay "})}`]}
            fieldOverrides={{
              fieldStyle: _fieldStyle,
              fieldWidth: _fieldWidth
            }}
            valueOverrides={{
              defaultValues: [getFieldValue2ElectricBogaloo(1, "value"), getFieldValue2ElectricBogaloo(1, "value_min")],
              valueTypes: ["number", "number"]
            }}
          />
        }
      />

      {/* Gate Timing */}
      { station.stationType === 2 ? <SecondarySetting
        key={station.station_id}
        header={t("esfWalkThroughPages.sections.gateMotor", {defaultValue: "gate motor"})}
        startEnhancer={glyphs.Setting({ fillColor: _glyphColor })}
        content={
          <div style={{ display: "flex", alignItems: "center" }}>
            <NewInputList
              ref={gateMotorRef}
              placeholders={Array(4).fill("")}
              headers={[
                `${t("esfWalkThroughPages.sections.gateAdjustment", { defaultValue: "gate adjustment" })} (${t("units.milisec", { defaultValue: "milisec" })}.)`,
                `${t("esfWalkThroughPages.sections.delayTimeClosingGate", { defaultValue: "delay time closing gate" })} (${t("units.milisec", { defaultValue: "milisec" })}.)`,
                `${t("esfWalkThroughPages.sections.indetificationValidTime", { defaultValue: "identification valid time" })} (${t("units.sec", { defaultValue: "sec" })}.)`,
                `${t("esfWalkThroughPages.sections.entryTime", {defaultValue: "entry time"})} (${t("units.sec", { defaultValue: "sec" })}.)`
              ]}
              fieldOverrides={{
                fieldStyle: _fieldStyle,
                fieldWidth: _fieldWidth
              }}
              listOverrides={{
                listStyle: ListStyle_Types.group,
                listGap: "10px"
              }}
              valueOverrides={{
                defaultValues: [getFieldValue2ElectricBogaloo(4, "value"), getFieldValue2ElectricBogaloo(4, "value_min"), getFieldValue2ElectricBogaloo(2, "value_min"), getFieldValue2ElectricBogaloo(2, "value_max")],
                valueTypes: Array(4).fill("number")
              }}
            />
          </div>
        }
      /> : <></>}

      {/* Waiting Time */}
      <SecondarySetting
        key={station.station_id}
        header={t("esfWalkThroughPages.sections.feedMotors", {defaultValue: "feed motors"})}
        startEnhancer={glyphs.Setting({ fillColor: _glyphColor })}
        content={
          <div style={{ display: "flex", alignItems: "center"}}>
            <NewInputList
              key={station.station_id}
              ref={feedMotorsRef}
              placeholders={Array(2).fill("")}
              headers={[
                `${t("esfWalkThroughPages.sections.noFeedBlockTime", { defaultValue: "no feed block time" })} (${t("units.sec", { defaultValue: "sec" })}.)`,
                `${t("esfWalkThroughPages.sections.lastFeedBlockTime", {defaultValue: "last feed block time"})} (${t("units.sec", { defaultValue: "sec" })}.)`
              ]}
              fieldOverrides={{
                fieldStyle: _fieldStyle,
                fieldWidth: _fieldWidth
              }}
              listOverrides={{
                listStyle: ListStyle_Types.group,
                listGap: "10px"
              }}
              valueOverrides={{
                defaultValues: [getFieldValue2ElectricBogaloo(3, "value_min"), getFieldValue2ElectricBogaloo(3, "value_max")],
                valueTypes: Array(2).fill("number")
              }}
            />
          </div>
        }
      />

      {/* Components */}
      <SecondarySetting
        key={station.station_id}
        header={t("esfWalkThroughPages.sections.motorComponents", {defaultValue: "motor components"})}
        startEnhancer={glyphs.Setting({ fillColor: _glyphColor })}
        content={
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {stationTerminals.map((terminal, tIndex) => {
              const selectedComponent = getSelectedComponent(terminal);
              const selectedColor = selectedComponent ? getGraphColor(selectedComponent.id).graphColor : undefined;

              return (
                <NewSelect
                  key={`${terminal.terminal_id}-${terminalComponents[terminal.terminal_id] ?? terminal.fk_components}`}
                  onChange={(value) => { setTerminalComponents(prev => ({ ...prev, [terminal.terminal_id]: value?.id != null ? Number(value.id) : null }))}}
                  header={`${t("esfWalkThroughPages.sections.motor", {defaultValue: "motor"})} #${tIndex + 1}`}
                  options={feedComponents?.map((component) => {
                    const { graphColor } = getGraphColor(component.id);
                  
                    return {
                      label: component.name,
                      // label: "",
                      id: component.id,
                      icon: <div style={{ width: "10px", height: "10px", backgroundColor: graphColor }}/>
                      // icon: <frames.ComponentLabel $component={component.id}>{component.name}</frames.ComponentLabel>
                    };
                  }) ?? []}
                  valueOverrides={{
                    defaultValue: selectedComponent ? {
                      label: selectedComponent.name,
                      id: selectedComponent.id,
                      icon: <div style={{ width: "10px", height: "10px", backgroundColor: selectedColor }}/>
                      // icon: <frames.ComponentLabel $component={selectedComponent.id}>{selectedComponent.name}</frames.ComponentLabel>
                    } : undefined,
                  }}
                  fieldOverrides={{
                    fieldStyle: _fieldStyle,
                    fieldWidth: _fieldWidth
                  }}
                />
              );
            })}
          </div>
        }
      />
    </div>
  )
});

export default SettingsContent